import type {
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";
import {
  QUESTIONNAIRE_ANSWER_OCCURRENCE_URL,
  QUESTIONNAIRE_FREE_TEXT_SYSTEM,
  QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL,
  QUESTIONNAIRE_INSTANCE_PROFILE,
  QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL,
  QUESTIONNAIRE_OPTION_SYSTEM,
  unitFromQuantity,
  unitToQuantity,
} from "@repo/api";
import type { RecordQuestionnaireAnswerInput } from "@repo/api/schemas";
import {
  asPatientId,
  asQuestionnaireInstanceId,
  type MemberId,
  type PatientId,
  parsePinnedCanonical,
  type QuestionnaireAnswer,
  type QuestionnaireAnswerContent,
  type QuestionnaireInstanceId,
  type QuestionnaireInstanceStatus,
  type VersionedCanonical,
} from "@repo/domain";
import { PATIENT_ENTITY_ID_SYSTEM, QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";

/**
 * Where a `QuestionnaireResponse` charted as a QuestionnaireInstance keeps what
 * the views read (ADR-0021: views read FHIR rows directly and never import
 * codecs).
 *
 * `QuestionnaireResponse` STORES TWO MODELS — the QuestionnaireInstance and the
 * Exam — and `meta.profile` is what says which. Every read here goes through
 * `assertQuestionnaireInstanceRow` first, because without it an exam row reads
 * as an instance: no error, just an assessment built from another model's row.
 * `./exam-storage.ts` owns the other half, gated on the other profile, and the
 * two gates are what keep either from ever entering the wrong reader.
 *
 * A row missing its identity, pin, patient or assignment timestamp THROWS
 * rather than reading as `""`. A substituted value is indistinguishable from a
 * real one, and an assessment that silently loses its patient is a record
 * attributed to nobody. `deriveForSync` in `./base.ts` catches this, logs the
 * resource id and drops that one row, so loud here costs one row rather than
 * the collection.
 *
 * ANSWERS ARE FLAT AND SPARSE, which is the storage shape the codec writes: one
 * item per answered address, the repetition carried in an extension rather than
 * inferred from position. An answer carrying no value at all is the same as no
 * item — "nobody has touched it" is exactly what absence means — so it is
 * dropped rather than hydrated as a blank.
 */

// Deliberately not `QuestionnaireResponse/<id>`: that spelling is a FHIR
// reference, and this is a row locator in a message about a row that could not
// be read.
function unreadable(response: QuestionnaireResponse, field: string): never {
  throw new Error(`Questionnaire instance row ${response.id ?? "<unknown>"} has no readable ${field}`);
}

/** Which model a row IS. Never a domain field, and never left to the caller. */
export function isQuestionnaireInstanceRow(response: QuestionnaireResponse): boolean {
  return response.meta?.profile?.includes(QUESTIONNAIRE_INSTANCE_PROFILE) ?? false;
}

/**
 * The read-side twin of the mapper's guard. Refusing here is what stops an exam
 * row being read as a questionnaire instance.
 */
export function assertQuestionnaireInstanceRow(response: QuestionnaireResponse): QuestionnaireResponse {
  if (response.resourceType !== "QuestionnaireResponse" || !isQuestionnaireInstanceRow(response)) {
    unreadable(response, "questionnaire instance profile");
  }
  return response;
}

// ---------- row facts ----------

export function questionnaireInstanceId(response: QuestionnaireResponse): QuestionnaireInstanceId {
  const identifier = response.identifier;
  const value = identifier?.system === QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM ? identifier.value : undefined;
  return value ? asQuestionnaireInstanceId(value) : unreadable(response, "QuestionnaireInstance identity");
}

/** The definition revision the instance was assigned against, always pinned. */
export function questionnaireInstanceDefinitionPin(response: QuestionnaireResponse): VersionedCanonical {
  return parsePinnedCanonical(response.questionnaire) ?? unreadable(response, "pinned definition");
}

export function questionnaireInstancePatientId(response: QuestionnaireResponse): PatientId {
  const subject = response.subject;
  const value = subject?.identifier?.system === PATIENT_ENTITY_ID_SYSTEM ? subject.identifier.value : undefined;
  return value ? asPatientId(value) : unreadable(response, "patient");
}

/** Stamped when the instance is assigned, and never rewritten. */
export function questionnaireInstanceAssignedAt(response: QuestionnaireResponse): string {
  return response.authored ?? unreadable(response, "assignment timestamp");
}

/**
 * `underAmendment` has no FHIR spelling, so a reopened record is stored as
 * `in-progress` plus the flag; every other status maps one for one, and a code
 * this model has no case for reads as in progress — which is what an unfinished
 * record is.
 */
export function questionnaireInstanceStatus(response: QuestionnaireResponse): QuestionnaireInstanceStatus {
  switch (response.status) {
    case "completed":
      return "completed";
    case "amended":
      return "amended";
    case "stopped":
      return "stopped";
    case "entered-in-error":
      return "enteredInError";
    default:
      return response.extension?.some(
        (candidate) => candidate.url === QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL && candidate.valueBoolean === true,
      )
        ? "underAmendment"
        : "inProgress";
  }
}

/** Set once the instance is filed; absent while it is still being answered. */
export function questionnaireInstanceCompletedAt(response: QuestionnaireResponse): string | undefined {
  return response.extension?.find((candidate) => candidate.url === QUESTIONNAIRE_INSTANCE_COMPLETED_AT_URL)
    ?.valueDateTime;
}

// ---------- answers ----------

function occurrenceOf(item: QuestionnaireResponseItem): number | undefined {
  return item.extension?.find((candidate) => candidate.url === QUESTIONNAIRE_ANSWER_OCCURRENCE_URL)?.valueInteger;
}

/**
 * Free text is a coding in its own system rather than a `valueString`: a plain
 * string is indistinguishable from a `Text` answer on the way back in, and this
 * reader has no definition to disambiguate against.
 */
export function answerContent(
  response: QuestionnaireResponse,
  answer: QuestionnaireResponseItemAnswer,
): QuestionnaireAnswerContent | undefined {
  const coding = answer.valueCoding;
  if (coding) {
    if (coding.system === QUESTIONNAIRE_FREE_TEXT_SYSTEM) {
      return { kind: "Choice", freeText: coding.display ?? unreadable(response, "free-text selection") };
    }
    return { kind: "Choice", optionId: (coding.code ?? unreadable(response, "selected option")) as MemberId };
  }
  if (answer.valueQuantity !== undefined) {
    const unit = unitFromQuantity(answer.valueQuantity);
    const value = answer.valueQuantity.value ?? unreadable(response, "quantity answer");
    return { kind: "Quantity", value, ...(unit === undefined ? {} : { unit }) };
  }
  if (answer.valueInteger !== undefined) return { kind: "Integer", value: answer.valueInteger };
  if (answer.valueBoolean !== undefined) return { kind: "Boolean", value: answer.valueBoolean };
  if (answer.valueDate !== undefined) return { kind: "Date", value: answer.valueDate };
  if (answer.valueUri !== undefined) return { kind: "Url", value: answer.valueUri };
  if (answer.valueAttachment !== undefined) {
    const attachment = answer.valueAttachment;
    return {
      kind: "Attachment",
      url: attachment.url ?? unreadable(response, "attachment answer"),
      ...(attachment.contentType === undefined ? {} : { contentType: attachment.contentType }),
      ...(attachment.title === undefined ? {} : { title: attachment.title }),
    };
  }
  if (answer.valueString !== undefined) return { kind: "Text", value: answer.valueString };
  return undefined;
}

/** Every answer in the tree, whatever depth a previous writer nested it at. */
function collect(
  response: QuestionnaireResponse,
  items: QuestionnaireResponseItem[] | undefined,
  into: QuestionnaireAnswer[],
): QuestionnaireAnswer[] {
  for (const item of items ?? []) {
    const occurrence = occurrenceOf(item);
    for (const answer of item.answer ?? []) {
      const value = answerContent(response, answer);
      if (value) {
        into.push({ questionId: item.linkId as MemberId, ...(occurrence === undefined ? {} : { occurrence }), value });
      }
      collect(response, answer.item, into);
    }
    collect(response, item.item, into);
  }
  return into;
}

export function questionnaireInstanceAnswers(response: QuestionnaireResponse): readonly QuestionnaireAnswer[] {
  return collect(response, response.item, []);
}

// ---------- the whole row ----------

/** A stored instance, as the read side knows it. */
export interface StoredQuestionnaireInstance {
  id: QuestionnaireInstanceId;
  definition: VersionedCanonical;
  patientId: PatientId;
  assignedAt: string;
  status: QuestionnaireInstanceStatus;
  completedAt?: string;
  answers: readonly QuestionnaireAnswer[];
}

/**
 * Every read at once, so that sync-time validation and the projection cannot
 * disagree about what a readable row is.
 */
export function readQuestionnaireInstanceRow(response: QuestionnaireResponse): StoredQuestionnaireInstance {
  const row = assertQuestionnaireInstanceRow(response);
  const completedAt = questionnaireInstanceCompletedAt(row);
  return {
    id: questionnaireInstanceId(row),
    definition: questionnaireInstanceDefinitionPin(row),
    patientId: questionnaireInstancePatientId(row),
    assignedAt: questionnaireInstanceAssignedAt(row),
    status: questionnaireInstanceStatus(row),
    ...(completedAt === undefined ? {} : { completedAt }),
    answers: questionnaireInstanceAnswers(row),
  };
}

/**
 * Read every field the questionnaire instance projection will later need,
 * discarding the values.
 *
 * The projection runs inside a TanStack DB `fn.select`, where a throw blanks
 * the whole page instead of costing one row. Validating at sync time is what
 * keeps an unreadable row from ever reaching it.
 */
export function assertQuestionnaireInstanceRowReadable(response: QuestionnaireResponse): void {
  readQuestionnaireInstanceRow(response);
}

export function toStoredAnswer(value: RecordQuestionnaireAnswerInput["value"]): QuestionnaireResponseItemAnswer {
  switch (value.kind) {
    case "Quantity":
      return { valueQuantity: { value: value.value, ...unitToQuantity(value.unit) } };
    case "Integer":
      return { valueInteger: value.value };
    case "Boolean":
      return { valueBoolean: value.value };
    case "Text":
      return { valueString: value.value };
    case "Date":
      return { valueDate: value.value };
    case "Url":
      return { valueUri: value.value };
    case "Attachment":
      return {
        valueAttachment: {
          url: value.url,
          ...(value.contentType ? { contentType: value.contentType } : {}),
          ...(value.title ? { title: value.title } : {}),
        },
      };
    case "Choice":
      return {
        valueCoding: value.freeText
          ? { system: QUESTIONNAIRE_FREE_TEXT_SYSTEM, display: value.freeText }
          : { system: QUESTIONNAIRE_OPTION_SYSTEM, code: value.optionId },
      };
  }
}
